<script>
  // Pick two (cube, axis) pairs, name each side, and get a query.
  //
  // The naming is the point, not the typing saved. "These two axes answer the
  // same question" is a judgment; a generator that inferred it would put an
  // alignment in the Pod that nobody made. Here it is a text input, and it ends
  // up in the saved query as the block headings and in its description.
  //
  // Read-only until Save, which goes through the ordinary named-query path and
  // its existing analyst role gate. Nothing here reaches a Pod on its own.
  import Panel from './Panel.svelte';
  import Button from './Button.svelte';
  import Alert from './Alert.svelte';
  import Code from './Code.svelte';
  import QueryPanel from './QueryPanel.svelte';
  import SegmentedControl from './SegmentedControl.svelte';
  import DeleteQuery from './DeleteQuery.svelte';
  import { comparableCubes, byPublisher } from '../../../lib/comparable.js';
  import { buildCompareQuery } from '../../../lib/compare_query.js';
  import { untrack } from 'svelte';
  import { allowedViews, VIEWS } from '../../../lib/value_schemes.js';
  import { NS } from '../../shared/config.js';

  // cubes/catalog — the two API responses, already fetched by the screen.
  // onrun    (sparql) → { columns, rows }   preview, writes nothing
  // onsave   ({ id, label, dataset, description, sparql, series, sides, term, view }) → entry
  // onedit   (entry) → void   hand the compiled query to the custom-query editor
  // ondelete (id) → Promise    delete the saved comparison being edited
  // existing — the named queries already in the Pod, for the id-collision warning
  // editing  — a saved comparison to open (one with `sides`); a new object each
  //            time Edit is pressed, so pressing it again reopens it
  let { cubes, catalog = null, onrun, onsave, onedit = null, ondelete = null, existing = [], editing = null } = $props();

  const entries = $derived(comparableCubes({ cubes, catalog }));
  const groups = $derived(byPublisher(entries));
  const eligible = $derived(entries.filter((e) => e.eligible));
  const at = (key) => entries.find((e) => e.key === key) ?? null;

  // Two sides to start; a comparison of one is not one. `label` is null while
  // untouched so the default keeps tracking the picked axis — once typed, it is
  // the analyst's and nothing overwrites it.
  let sides = $state([{ key: null, axis: null, label: null }, { key: null, axis: null, label: null }]);

  function preselect() {
    // The first eligible cube of each distinct publisher, which in this demo is
    // the comparison someone came here to make. A starting point, not a claim.
    const seen = new Set();
    const picks = eligible.filter((e) => !seen.has(e.publisher) && seen.add(e.publisher)).slice(0, 2);
    if (picks.length === 2) {
      sides = picks.map((e) => ({ key: e.key, axis: e.axes[0], label: null }));
    }
  }
  $effect(() => { if (eligible.length && sides.every((s) => !s.key)) preselect(); });

  // The saved comparison this is, while editing one: saving replaces it, under
  // whatever name it now has.
  let editingId = $state(null);
  // What the counts are of ("people"), kept as the comparison had it.
  let unit = $state(null);
  $effect(() => { if (editing) untrack(() => open(editing)); });
  function open(entry) {
    sides = entry.sides.map((s) => ({ key: `${s.publisher}::${s.cubeId}`, axis: s.axis, label: s.label }));
    label = entry.label;
    description = entry.description ?? null;
    chosenView = entry.view ?? null;
    editingId = entry.id;
    unit = entry.unit ?? null;
  }
  function startNew() {
    editingId = null; unit = null;
    label = ''; description = null; chosenView = null;
  }
  // A side whose cube is gone since the comparison was saved.
  const missing = $derived(sides.filter((s) => s.key && !at(s.key)).map((s) => s.key.replace('::', ' · ')));

  function setCube(i, key) {
    const e = at(key);
    sides[i] = { ...sides[i], key, axis: e?.axes[0] ?? null };
  }

  // Default label, and the de-duplication the generator would otherwise reject:
  // two sides of one cube's two axes, or two institutions' identically named
  // axes, collide on the obvious default.
  const defaultLabel = (side, i) => {
    const e = at(side.key);
    if (!e || !side.axis) return `Side ${i + 1}`;
    const base = `${e.publisher} · ${side.axis}`;
    const clashes = sides.some((o, j) => j !== i && at(o.key) && o.axis
      && `${at(o.key).publisher} · ${o.axis}` === base);
    return clashes ? `${base} (${e.cubeId})` : base;
  };
  const labelOf = (side, i) => side.label ?? defaultLabel(side, i);

  const pairs = $derived(sides.map((s, i) => {
    const e = at(s.key);
    return e && s.axis
      ? { publisher: e.publisher, cubeId: e.cubeId, axis: s.axis, seriesLabel: labelOf(s, i),
          dimensions: e.axes, periods: e.periods, measure: e.measure }
      : null;
  }));
  const ready = $derived(pairs.every(Boolean) && pairs.length >= 2);

  // Every side reading the same term with a value scheme: the comparison is
  // about that term, saved with it, and its values come back in the scheme's
  // order and labels. Different terms on the sides stay a comparison you named,
  // drawn Separate, largest first.
  const shared = $derived(ready && new Set(pairs.map((p) => p.axis)).size === 1 ? pairs[0].axis : null);
  const scheme = $derived(shared ? cubes.schemes?.[shared] ?? null : null);
  const term = $derived(scheme ? shared : null);
  const views = $derived(allowedViews(scheme));
  // What it opens as: the author's pick, else the most the scheme allows.
  let chosenView = $state(null);
  const view = $derived(views.includes(chosenView) ? chosenView : views.at(-1));

  // Compiled live, so the SPARQL is visible before anything is run. A build
  // error (duplicate names) is a message, not a thrown render.
  const built = $derived.by(() => {
    if (!ready) return { error: null };
    try {
      return buildCompareQuery({
        pairs, measure: cubes.measure, period: cubes.period,
        prefixes: cubes.prefixes ?? NS, whole: Boolean(term),
      });
    } catch (e) { return { error: e.message }; }
  });

  // A guessed measure is the one place this can be quietly wrong: the cube
  // reader classifies partner cells against OUR measure, so it never learns
  // theirs, and a partner counting something else yields an empty block.
  const assumed = $derived(pairs.filter((p, i) => p && at(sides[i].key)?.measureAssumed));

  let label = $state('');
  // The dataset it compares: that of our own side's cube (none when both
  // sides are partners').
  const dataset = $derived(sides.map((s) => at(s.key)).find((e) => e?.own && e.dataset)?.dataset ?? null);
  let description = $state(null); // null = still following the generated one
  const descriptionOf = $derived(description ?? built.description ?? '');

  const slug = (s) => String(s).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'query';
  const collision = $derived(label ? existing.find((q) => q.id === slug(label) && q.id !== editingId) : null);

  let preview = $state(null);
  let running = $state(false);
  let saving = $state(false);
  let error = $state(null);
  let saved = $state(null);
  let confirming = $state(false);

  // Any edit invalidates a preview taken of something else. Showing yesterday's
  // numbers under today's query is the one failure mode this screen must not
  // have — it is a screen about not being misled by a table.
  //
  // `saved` is invalidated by the same rule but over a wider set of inputs: it
  // means "what is currently composed is in the Pod", so anything that would
  // change what a save writes — the sides, the name, the description
  // — makes it untrue again and brings the form back.
  $effect(() => { built.sparql; preview = null; saved = null; confirming = false; });
  $effect(() => { label; description; view; saved = null; confirming = false; });

  async function run() {
    running = true; error = null;
    try { preview = await onrun(built.sparql); }
    catch (e) { error = e.message; preview = null; }
    finally { running = false; }
  }

  async function save() {
    // The overwrite warning is raised by pressing save, not by typing a name
    // that happens to collide — a name is not a decision until it is submitted.
    if (collision && !confirming) { confirming = true; return; }
    saving = true; error = null; saved = null;
    try {
      saved = await onsave({
        label, dataset, description: descriptionOf || null,
        sparql: built.sparql, series: built.series,
        sides: pairs.map((p) => ({ publisher: p.publisher, cubeId: p.cubeId, axis: p.axis, label: p.seriesLabel })),
        ...(editingId ? { id: editingId } : {}),
        ...(unit ? { unit } : {}),
        ...(term ? { term, view } : {}),
      });
      editingId = saved.id;
      confirming = false;
    } catch (e) { error = e.message; }
    finally { saving = false; }
  }
</script>

<Panel>
  <!-- The one thing that cannot go in the manual: naming the sides is not
       labelling, it is the claim. Everything else here — what a facet does,
       why a cube is unavailable, what the convention is — is in the “?”. -->
  <p class="wf2-text">
    Naming the two sides is you saying they measure the same thing. That
    judgement is yours — nothing here can make it for you.
  </p>
  {#if editingId}
    <p class="wf2-caption">
      Editing a saved comparison: saving replaces it.
      <button type="button" class="link" onclick={startNew}>Save as a new one instead</button>
    </p>
  {/if}
  {#if missing.length}
    <Alert title="A side's cube is not available any more" variant="warning">
      <p class="wf2-text">{missing.join(', ')}. Pick another cube for that side.</p>
    </Alert>
  {/if}

  {#if eligible.length < 2}
    <Alert title="Not enough shared cubes to compare" variant="info">
      <p class="wf2-text">
        A comparison reads the shared containers, and fewer than two cubes are in
        one. <a href="#/catalog">Cubes</a> shows which cubes exist and where
        each one went.
      </p>
    </Alert>
  {:else}
    {#each sides as side, i}
      <div class="wf2-row side" style="gap:12px; flex-wrap:wrap; align-items:flex-end">
        <label class="wf2-field" style="flex:2 1 260px">Cube
          <select class="wf2-control" value={side.key} onchange={(e) => setCube(i, e.currentTarget.value)}>
            <option value={null} disabled>Choose a cube…</option>
            <!-- Name and institution only. Dimensions, observations and state
                 are a click away on Cubes, and repeating them here would
                 be a second, staler copy of that table. -->
            {#each groups as group}
              <optgroup label={group.publisher}>
                {#each group.cubes as c}
                  <option value={c.key} disabled={!c.eligible} title={c.reason ?? ''}>
                    {c.cubeId}{c.eligible ? '' : ' — unavailable'}
                  </option>
                {/each}
              </optgroup>
            {/each}
          </select>
        </label>

        <label class="wf2-field" style="flex:1 1 180px">Broken down by
          <select class="wf2-control mono" bind:value={sides[i].axis} disabled={!at(side.key)}>
            {#each at(side.key)?.axes ?? [] as axis}
              <option value={axis}>{axis}</option>
            {/each}
          </select>
        </label>

        <label class="wf2-field" style="flex:2 1 220px">Call this side
          <input class="wf2-control" value={labelOf(side, i)}
            oninput={(e) => (sides[i].label = e.currentTarget.value)} />
        </label>

        {#if sides.length > 2}
          <Button variant="ghost" onclick={() => (sides = sides.filter((_, j) => j !== i))}>Remove</Button>
        {/if}
      </div>
    {/each}

    <div class="wf2-row" style="gap:12px; margin-top: var(--space-2)">
      <Button variant="ghost" onclick={() => (sides = [...sides, { key: null, axis: null, label: null }])}>
        Add another side
      </Button>
    </div>

    {#each entries.filter((e) => !e.eligible) as blocked}
      <p class="wf2-caption"><code class="mono">{blocked.cubeId}</code> ({blocked.publisher}) — {blocked.reason}</p>
    {/each}

    {#if built.error}
      <Alert title="Cannot compile this comparison" variant="warning"><p>{built.error}</p></Alert>
    {:else if ready}
      {#each built.warnings as w}
        <p class="wf2-hint">{w}</p>
      {/each}
      {#if assumed.length}
        <p class="wf2-hint">
          {assumed.map((p) => p.publisher).join(', ')} never said what their cube
          counts, so we are assuming <code class="mono">{cubes.measure}</code>.
          If they are counting something else their block comes back empty —
          which is the safe way round, but it means an empty block here might be
          a wrong guess rather than no data.
        </p>
      {/if}

      <!-- Kept, but folded away. The compiled SPARQL is worth being able to
           reach — it is the teaching surface, and reading it is how you check
           the tool did what you meant — but it is not what you are looking at
           while picking sides. "Edit as custom query" is the other way to it. -->
      <details class="sparql">
        <summary class="wf2-caption">Show the compiled SPARQL</summary>
        <Code lang="sparql" text={built.sparql} />
      </details>

      <div class="wf2-row" style="gap:12px; margin-top: var(--space-2)">
        <Button variant="secondary" onclick={run} disabled={running}>
          {running ? 'Running…' : 'Preview result'}
        </Button>
        {#if onedit}
          <!-- The way out. This tool covers two-to-N single-axis distributions;
               everything else is a hand edit away, and a compiled query that
               gets edited afterwards is a normal outcome, not a failure. -->
          <Button variant="ghost" onclick={() => onedit({
            ...(editingId ? { id: editingId } : {}), label, dataset, description: descriptionOf || null,
            sparql: built.sparql, series: built.series, ...(unit ? { unit } : {}), ...(term ? { term, view } : {}) })}>Edit as custom query</Button>
        {/if}
      </div>

      {#if preview}
        <p class="wf2-caption">Exactly as Analysis will render it — the same component.</p>
        <QueryPanel
          query={{ label: label || 'Untitled comparison', description: descriptionOf,
                   sparql: built.sparql, series: built.series, term, view }}
          result={{ ...preview, scheme }} />
      {/if}

      <!-- One or the other. A save form still sitting under "Saved to your Pod"
           invites a second save of the same thing, and reads as though the first
           one did not take. -->
      {#if saved}
        <Alert title="Saved to your Pod" variant="success">
          <p class="wf2-text">
            "{saved.label}" is saved.
            <a href="#/analysis">See it on Comparisons →</a>
          </p>
          <p class="wf2-caption">Change a side or the name to build another.</p>
        </Alert>
      {:else}
        <h4 class="wf2-title wf2-title-level-4">Save as a named query</h4>
        <div class="wf2-row" style="gap:12px; flex-wrap:wrap">
          <input class="wf2-control" style="flex:2 1 220px" placeholder="Query name (e.g. Gender, both institutions)" bind:value={label} />
        </div>
        <!-- Generated, then editable. The caveats are written for you because
             they follow from what was picked; anything else in here is the
             author's. -->
        <textarea class="wf2-control" rows="4" style="margin-top: var(--space-2)"
          value={descriptionOf} oninput={(e) => (description = e.currentTarget.value)}></textarea>
        <p class="wf2-caption">Written from what you picked. Edit freely — it ships with the query.</p>
        {#if views.length > 1}
          <!-- The view is the author's; a viewer can still switch a panel,
               but it opens as this again on the next run. -->
          <div style="margin-top: var(--space-2)">
            <SegmentedControl label="Opens as" options={views.map((id) => ({ id, label: VIEWS[id] }))}
              selected={view} onselect={(id) => (chosenView = id)} />
          </div>
        {/if}

        {#if collision && confirming}
          <Alert title="A query with this name already exists" variant="warning">
            <p class="wf2-text">
              Saving replaces <strong>{collision.label}</strong> — the Pod keys
              queries by name, so this is an overwrite, not a second entry.
              Press save again to go ahead.
            </p>
          </Alert>
        {/if}

        <div class="wf2-row" style="gap:12px; margin-top: var(--space-2)">
          <Button variant="primary" onclick={save} disabled={saving || !label || (!preview && !editingId)}>
            {saving ? 'Saving…' : confirming ? 'Save and replace' : editingId ? 'Save changes' : 'Save as named query'}
          </Button>
        </div>
        {#if !preview && !editingId}
          <p class="wf2-caption">Preview it first — picking the wrong breakdown is easiest to spot in the numbers.</p>
        {/if}

        {#if error}
          <Alert title="Could not do that" variant="danger"><p class="mono">{error}</p></Alert>
        {/if}
      {/if}
    {/if}
  {/if}
</Panel>

{#if editingId && ondelete}<DeleteQuery {label} ondelete={() => ondelete(editingId)} />{/if}

<style>
  .side + .side { margin-top: var(--space-2); }
  .sparql { margin-top: var(--space-3); }
  .sparql summary { cursor: pointer; }
  .link { font: inherit; background: none; border: 0; padding: 0; color: var(--accent); text-decoration: underline; cursor: pointer; }
</style>
